import { AccessToken, AuthPluginError, GetTokenOptions } from '../types';
import { AuthPluginFacade } from '../facades/AuthPluginFacade';
import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { JWebErrorHandler } from '../../../services/JWeb';
import * as JWebTypes from '../../../services/JWeb/types';
import { getServices } from '../../../infra/startup';

class InitializationTokenCache {
  public static cachedToken: AccessToken | null = null;
  public static isAlive = true;

  public static async getToken(
    options?: GetTokenOptions
  ): Promise<AuthPluginError | AccessToken> {
    if (!InitializationTokenCache.isAlive) {
      return null;
    }

    if (InitializationTokenCache.cachedToken) {
      internalLogger?.debug?.d5?.({
        className: 'InitializationTokenCache',
        methodName: 'getToken()',
        message: 'Using cached token during initialization',
        cachedToken: InitializationTokenCache.cachedToken
      });
      return InitializationTokenCache.cachedToken;
    }

    internalLogger?.debug?.d5?.({
      className: 'InitializationTokenCache',
      methodName: 'getToken()',
      message: 'First getToken call during initialization - caching result'
    });
    const result = await AuthPluginFacade.getToken(options);

    if (result && !(result as AuthPluginError).error) {
      InitializationTokenCache.cachedToken = result as AccessToken;
    }

    return result;
  }

  public static async restartCache(): Promise<void> {
    InitializationTokenCache.isAlive = true;
    internalLogger?.debug?.d5?.({
      className: 'InitializationTokenCache',
      methodName: 'restartCache()',
      message: 'Token cache re-activated'
    });
    await InitializationTokenCache.rehydrateToken();
  }

  public static async rehydrateToken(): Promise<void> {
    if (!InitializationTokenCache.isAlive) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4?.({
        className: 'InitializationTokenCache',
        methodName: 'rehydrateToken()',
        message:
          'Tried to rehydrate while TokenCache is dead. Skipping the rehydrate.'
      });
      return null;
    }
    try {
      // Rehydrate InitializationTokenCache which will be called by NativeAuthStrategy from RefreshWhenAuthStatusChanged.

      const actualToken = await AuthPluginFacade.getToken().then((v) =>
        JWebErrorHandler<JWebTypes.AccessToken>(v)
      );

      // Only add if result is an AccessToken (not AuthPluginError)
      if (actualToken?.tokenValue) {
        /* istanbul ignore next */
        internalLogger?.debug?.d4?.({
          className: 'InitializationTokenCache',
          methodName: 'rehydrateToken()',
          message: 'Rehydrating token cache.'
        });
        // Actual rehydrate
        InitializationTokenCache.cachedToken = actualToken;
      } else {
        throw new Error('AuthPluginError received during rehydrateToken');
      }

      // During rehydrate, in case the received token is org-aware, we need to do a setTenant.
      if (actualToken.tenantId) {
        /* istanbul ignore next */
        internalLogger?.debug?.d4?.({
          className: 'InitializationTokenCache',
          methodName: 'rehydrateToken()',
          message: 'Rehydrate token: setting tenant.',
          variables: {}
        });
        const services = getServices();
        await services.tenantHandlerService.setTenant(
          actualToken.tenantId,
          services.tenantHandlerService.START_TENANT_LEVEL,
          {}
        );
      }
    } catch (error) {
      /* istanbul ignore next */
      internalLogger?.error?.e3?.({
        className: 'InitializationTokenCache',
        methodName: 'rehydrateToken()',
        message: 'RehydrateToken failed.',
        error
      });
      InitializationTokenCache.isAlive = false;
    }
  }

  public static killCache(): void {
    InitializationTokenCache.cachedToken = null;
    InitializationTokenCache.isAlive = false;
    internalLogger?.debug?.d5?.({
      className: 'InitializationTokenCache',
      methodName: 'killCache()',
      message: 'Token cache cleared after initialization'
    });
  }

  public static getCachedToken(): AccessToken {
    return InitializationTokenCache.cachedToken;
  }
}

export default InitializationTokenCache;
